import { oc } from "@orpc/contract";
import { JSON_SCHEMA_OUTPUT_REGISTRY } from "@orpc/zod/zod4";
import { z } from "zod";
import { Action, Subject, UserGroupStaticKind } from "@erp/authz";

/**
 * Authorization RPC Contracts
 *
 * Defines the contract for permission checking and management endpoints
 */

/**
 * Action enum values for Zod validation
 * Derived from the shared Action enum in @erp/authz
 */
const ActionSchema = z.enum(Action);

/**
 * Subject enum values for Zod validation
 * Derived from the shared Subject enum in @erp/authz
 */
const SubjectSchema = z.enum(Subject);

// New subjects show up in authz catalog responses. A closed enum makes that a
// breaking change for generated clients. x-extensible-enum tells oasdiff the
// set is allowed to grow; request bodies keep the closed enum.
const extensibleSubjectSchema = {
  enum: undefined,
  "x-extensible-enum": Object.values(Subject),
};
JSON_SCHEMA_OUTPUT_REGISTRY.add(
  SubjectSchema,
  extensibleSubjectSchema as { enum: undefined },
);

// ============================================
// Permission Check Contract
// ============================================

/**
 * Input schema for permission check
 *
 * Subject-level only. The schema used to take an optional `record` for
 * instance-level checks, but nothing ever sent one and the check did not work:
 * CASL resolves a plain object's subject type from its constructor, so a
 * record literal typed as `Object` matched no rule and came back denied even
 * for a user holding the permission. Reinstating instance checks means giving
 * CASL a real subject type and conditional rules to match against, at which
 * point the input belongs in a POST body rather than this GET's query string.
 */
export const AuthzCanInputSchema = z.object({
  action: ActionSchema,
  subject: SubjectSchema,
});

/**
 * Output schema for permission check
 */
export const AuthzCanOutputSchema = z.object({
  allowed: z.boolean(),
});

/**
 * Permission check contract
 *
 * Routed as GET so it is eligible for the client's batch link, which only
 * groups GET requests (see frontend/src/api/rpc/batching.ts). A page's first
 * render fires one `can` call per distinct action/subject pair; batching
 * collapses those into a single request instead of one per pair.
 *
 * The input is two short enum values, so it carries nothing sensitive into
 * URLs, proxy logs, or browser history, and stays well under the batch link's
 * URL-length ceiling. Keep it that way: anything record-shaped added here
 * would travel in the query string.
 */
export const authzCanContract = oc
  .input(AuthzCanInputSchema)
  .output(AuthzCanOutputSchema)
  .route({
    method: "GET",
    summary: "Check if you have a permission",
    description:
      "Checks whether the current user may perform an action on a subject (for example read invoices) and returns allowed true or false.",
  });

// ============================================
// List Available Permissions Contract
// ============================================

/**
 * Schema for a single permission definition
 */
const PermissionDefinitionSchema = z.object({
  action: ActionSchema,
  subject: SubjectSchema,
});

/**
 * Schema for domain/subdomain grouping
 */
const DomainGroupSchema = z.object({
  domain: z.string(),
  subdomains: z.array(
    z.object({
      subdomain: z.string(),
      subjects: z.array(SubjectSchema),
    }),
  ),
});

/**
 * Schema for valid actions per subject mapping. Partial: subjects outside the
 * catalog (e.g. staff-only ones in the customer catalog) have no entry.
 */
const ValidActionsPerSubjectSchema = z.partialRecord(
  SubjectSchema,
  z.array(ActionSchema),
);

/**
 * Output schema for listing available permissions
 */
export const ListAvailablePermissionsOutputSchema = z.object({
  permissions: z.array(PermissionDefinitionSchema),
  actions: z.array(ActionSchema),
  subjects: z.array(SubjectSchema),
  domainGroups: z.array(DomainGroupSchema),
  validActionsPerSubject: ValidActionsPerSubjectSchema,
  // The separate catalog the static Staff Admin group is edited with.
  staffAdmin: z.object({
    permissions: z.array(PermissionDefinitionSchema),
    domainGroups: z.array(DomainGroupSchema),
  }),
});

/**
 * List available permissions contract
 * Returns all possible action/subject combinations
 */
export const listAvailablePermissionsContract = oc
  .output(ListAvailablePermissionsOutputSchema)
  .route({
    summary: "List all assignable permissions",
    description:
      "Returns every action and subject permission users can be granted, grouped by domain, with valid actions per subject and the separate staff admin catalog.",
  });

// ============================================
// Get Group Permissions Contract
// ============================================

/**
 * Input schema for getting group permissions
 */
export const GetGroupPermissionsInputSchema = z.object({
  groupId: z.uuid(),
});

/**
 * Schema for a single group permission
 */
const GroupPermissionSchema = z.object({
  id: z.uuid(),
  action: ActionSchema,
  subject: SubjectSchema,
  domain: z.string().nullable(),
  subdomain: z.string().nullable(),
  description: z.string().nullable(),
});

/**
 * Output schema for getting group permissions
 */
export const GetGroupPermissionsOutputSchema = z.object({
  groupId: z.uuid(),
  groupName: z.string(),
  // "staff_admin" for the static Staff Admin group, null for ordinary groups.
  staticKind: z.enum(UserGroupStaticKind).nullable(),
  permissions: z.array(GroupPermissionSchema),
});

/**
 * Get group permissions contract
 */
export const getGroupPermissionsContract = oc
  .input(GetGroupPermissionsInputSchema)
  .output(GetGroupPermissionsOutputSchema)
  .route({
    summary: "Get a user group's permissions",
    description:
      "Returns the name and every granted action and subject permission for one user group.",
  });

// ============================================
// Set Group Permissions Contract
// ============================================

/**
 * Schema for a permission to set
 */
const PermissionToSetSchema = z.object({
  action: ActionSchema,
  subject: SubjectSchema,
  domain: z.string().nullable().optional(),
  subdomain: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});

/**
 * Input schema for setting group permissions
 */
export const SetGroupPermissionsInputSchema = z.object({
  groupId: z.uuid(),
  permissions: z.array(PermissionToSetSchema),
});

/**
 * Output schema for setting group permissions
 */
export const SetGroupPermissionsOutputSchema = z.object({
  success: z.boolean(),
  groupId: z.uuid(),
  permissionCount: z.number(),
});

/**
 * Set group permissions contract
 * Replaces all permissions for a group with the provided list
 */
export const setGroupPermissionsContract = oc
  .input(SetGroupPermissionsInputSchema)
  .output(SetGroupPermissionsOutputSchema)
  .route({
    summary: "Replace a user group's permissions",
    description:
      "Overwrites all permissions of a user group with the given action and subject list. Ordinary groups cannot receive staff-only subjects or any manage permission.",
  });

// ============================================
// Authorization contracts namespace
// ============================================

export const authzContracts = {
  can: authzCanContract,
  listAvailablePermissions: listAvailablePermissionsContract,
  getGroupPermissions: getGroupPermissionsContract,
  setGroupPermissions: setGroupPermissionsContract,
} as const;

// Export types
export type AuthzCanInput = z.infer<typeof AuthzCanInputSchema>;
export type AuthzCanOutput = z.infer<typeof AuthzCanOutputSchema>;

export type ListAvailablePermissionsOutput = z.infer<
  typeof ListAvailablePermissionsOutputSchema
>;
export type GetGroupPermissionsInput = z.infer<
  typeof GetGroupPermissionsInputSchema
>;
export type GetGroupPermissionsOutput = z.infer<
  typeof GetGroupPermissionsOutputSchema
>;
export type SetGroupPermissionsInput = z.infer<
  typeof SetGroupPermissionsInputSchema
>;
export type SetGroupPermissionsOutput = z.infer<
  typeof SetGroupPermissionsOutputSchema
>;
